// Builds a single index of everything the wiki search panel knows about:
// exams (Exam*.md at repo root), concepts (/Concepts), and resources
// (/Resources/Books). Cached in localStorage for 6h, matching the TTL
// used by useWikiSyllabus.

import fm from 'front-matter'
import { listRepoContents, fetchWikiFile, rawGithubUrl } from '@/lib/github'
import { vaultMediaUrl } from '@/lib/vaultAssets'
import { buildResourceExamMap, examsForResource } from '@/lib/resourceExams'
import { examDisplayName } from '@/lib/wikiRoutes'
import { resourcePdfUrl } from '@/lib/examPdf'

export type WikiIndexCategory = 'exam' | 'concept' | 'document'

export interface WikiIndexItem {
  category: WikiIndexCategory
  name: string          // display name ("Expected Value", "Exam P-1 (SOA)")
  path: string          // repo path ("Concepts/Expected Value.md")
  // Optional metadata, pulled from YAML frontmatter on resource files.
  topic?: string        // exam id the item belongs to (for filtering)
  // Exams this resource is a syllabus reading for ("Exam P-1"), derived from the
  // exam pages' Source Material callouts — see lib/resourceExams.ts. Documents only.
  exams?: string[]
  author?: string
  year?: number
  title?: string        // display title (overrides name): a resource's own title, or
                        // an exam without its examining-body suffix ("Exam MAS-I")
  questionCount?: number // number of questions whose wiki_link points here
  coverImage?: string   // the cover's URL — the app's own /Media/… copy
  edition?: string
  publisher?: string
  // The page's `Available from` is a PDF the app's reader opens — the card's
  // PDF mark (`PdfPill`). Documents only.
  pdf?: boolean
}

const CACHE_KEY = 'actuarial_wiki_index_v6'
const CACHE_TTL_MS = 6 * 60 * 60 * 1000

interface CacheEntry {
  data: WikiIndexItem[]
  expiresAt: number
}

function readCache(): WikiIndexItem[] | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const entry: CacheEntry = JSON.parse(raw)
    if (Date.now() > entry.expiresAt) {
      localStorage.removeItem(CACHE_KEY)
      return null
    }
    return entry.data
  } catch {
    return null
  }
}

function writeCache(data: WikiIndexItem[]): void {
  const entry: CacheEntry = { data, expiresAt: Date.now() + CACHE_TTL_MS }
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(entry))
  } catch {
    /* quota exceeded — skip */
  }
}

function stripExt(name: string): string {
  return name.replace(/\.md$/i, '')
}

const IMAGE_EXT_RE = /\.(png|jpe?g|gif|svg|webp|avif)$/i

function extractCoverImageUrl(content: string): string | undefined {
  const m = /!\[\[([^\]]+)\]\]/.exec(content)
  if (!m) return undefined
  const imagePath = m[1].trim()
  if (!IMAGE_EXT_RE.test(imagePath)) return undefined
  // Short filenames (no directory) live under Media/Attachments/ by convention
  const resolved = imagePath.includes('/') ? imagePath : `Media/Attachments/${imagePath}`
  return vaultMediaUrl(resolved) ?? rawGithubUrl(resolved)
}

function parseFrontmatter(raw: string): Record<string, string> {
  // Delegates to front-matter (already a dep) so quoted values, escaped quotes,
  // and YAML edge cases are handled correctly. Coerces to string for callers.
  try {
    const attrs = fm<Record<string, unknown>>(raw).attributes ?? {}
    const out: Record<string, string> = {}
    for (const [k, v] of Object.entries(attrs)) {
      if (v != null) out[k] = String(v)
    }
    return out
  } catch {
    return {}
  }
}

let bundledIndex: WikiIndexItem[] | null = null
export function setWikiIndexBundle(items: WikiIndexItem[]): void { bundledIndex = items }

/**
 * The bundled index if it has been set, synchronously — for a surface that has
 * to have it on its *first* render rather than a microtask later (the Study
 * Guides home page, whose exam list has to be on the page in the frame a
 * tab-switch view transition captures). `buildWikiIndex` is still the way to ask for it in
 * general: it falls back to the cache and the network, this does not.
 */
export function bundledWikiIndex(): WikiIndexItem[] | null { return bundledIndex }

// Synchronous check against the bundled index. Returns true when the entry
// exists or when the index hasn't been loaded yet (fail-open so links aren't
// incorrectly styled as missing during hydration).
export function isInWikiIndex(kind: string, name: string): boolean {
  if (!bundledIndex) return true
  const nameLower = name.toLowerCase()
  // 'resource' maps to 'document' in the index. 'concept' also checks 'document'
  // because bare [[Name]] wikilinks default to kind 'concept' even when the
  // target is a resource/book entry.
  const categories =
    kind === 'resource' ? ['document'] :
    kind === 'concept' ? ['concept', 'document'] :
    [kind]
  return bundledIndex.some(
    item => categories.includes(item.category) && item.name.toLowerCase() === nameLower
  )
}

export async function buildWikiIndex(): Promise<WikiIndexItem[]> {
  if (bundledIndex) return bundledIndex
  const cached = readCache()
  if (cached) return cached

  const items: WikiIndexItem[] = []

  // Repo root — grab exam files.
  const rootItems = await listRepoContents().catch(() => [])
  const examItems: WikiIndexItem[] = []
  for (const it of rootItems) {
    if (it.type !== 'file' || !it.name.endsWith('.md')) continue
    if (!/^Exam\b/i.test(it.name)) continue
    const bare = stripExt(it.name)
    // `name` stays the file name (it is the route key); `title` is what a
    // surface shows, with the examining-body suffix stripped.
    const item: WikiIndexItem = { category: 'exam', name: bare, path: it.path, title: examDisplayName(bare) }
    items.push(item)
    examItems.push(item)
  }

  // Which exams each resource is a syllabus reading for. The exam pages are the
  // only place that relationship is authored, so this path reads them too —
  // a dozen files, fetched once per index build.
  const examPages = await Promise.all(examItems.map(async it => ({
    name: it.name,
    markdown: await fetchWikiFile(it.path).catch(() => ''),
  })))
  const examMap = buildResourceExamMap(examPages)

  // Concepts directory.
  const conceptItems = await listRepoContents('Concepts').catch(() => [])
  for (const it of conceptItems) {
    if (it.type !== 'file' || !it.name.endsWith('.md')) continue
    items.push({ category: 'concept', name: stripExt(it.name), path: it.path })
  }

  // Resources — only Books for now; other subdirs would be added here.
  const bookItems = await listRepoContents('Resources/Books').catch(() => [])
  const bookMetaPromises = bookItems
    .filter(it => it.type === 'file' && it.name.endsWith('.md'))
    .map(async it => {
      let fmData: Record<string, string> = {}
      let coverImage: string | undefined
      try {
        const raw = await fetchWikiFile(it.path)
        fmData = parseFrontmatter(raw)
        coverImage = extractCoverImageUrl(raw)
      } catch {
        /* ignore — keep item with filename-derived display */
      }
      const yearNum = fmData['Year'] ? parseInt(fmData['Year'], 10) : undefined
      const bare = stripExt(it.name)
      const exams = examsForResource(examMap, bare)
      const item: WikiIndexItem = {
        category: 'document',
        name: bare,
        path: it.path,
        exams: exams.length > 0 ? exams : undefined,
        author: fmData['Authors'] || fmData['Author'] || undefined,
        year: Number.isFinite(yearNum) ? yearNum : undefined,
        title: fmData['Title'] || undefined,
        edition: fmData['Edition'] || undefined,
        publisher: fmData['Publisher'] || undefined,
        coverImage,
        pdf: resourcePdfUrl(fmData['Available from']) ? true : undefined,
      }
      return item
    })
  items.push(...(await Promise.all(bookMetaPromises)))

  // Only cache when we got real data — an empty array means all API calls
  // failed (rate limit, network error) and caching it would block retries
  // for 6 hours since [] is truthy and would be returned immediately.
  if (items.length > 0) writeCache(items)
  return items
}

export function clearWikiIndexCache(): void {
  localStorage.removeItem(CACHE_KEY)
  localStorage.removeItem('actuarial_wiki_index_v3')
  localStorage.removeItem('actuarial_wiki_index_v2')
  localStorage.removeItem('actuarial_wiki_index_v1')
}
